import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAppSettings } from "../components/providers/AppSettingsProvider";
import {
  ChevronRight, Languages, LogOut, Shield, Star, Sun, Bell, FileText, Gift, Trash2
} from "lucide-react";
import { loadAuthUser, logout, deleteAccount, type AuthUser } from "../services/auth";
import { deleteUserAccountFromBackend, updateUserProfile } from "../services/userService";
import { getUserDisplayName } from "../utils";
import { LANGUAGES } from "../services/realtimeVoice";
import { getPageBackgroundGradient } from "../config/theme";

import { getCustomerInfo } from "../services/RevenueCatService";
import { NotificationService } from "../services/NotificationService";
import { TimePicker } from "../components/ui/TimePicker";
import { AnimatePresence } from "framer-motion";
import MembershipCard, { type SubscriptionDetails } from "../components/MembershipCard";

// 主题切换组件
const ThemeToggle: React.FC = () => {
  const { theme, setTheme, t } = useAppSettings();
  const isDark = theme === 'dark';

  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-3">
        <Sun
          className="w-5 h-5"
          style={!isDark ? { color: '#587FFF', stroke: '#587FFF' } : { color: 'rgba(255, 255, 255, 0.7)' }}
        />
        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
          {t('profile.theme.darkMode')}
        </span>
      </div>
      <button
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        className={`w-12 h-6 rounded-full transition-colors ${theme === 'dark' ? 'bg-blue-500' : 'bg-gray-300'
          }`}
      >
        <div
          className={`w-5 h-5 bg-white rounded-full transition-transform ${theme === 'dark' ? 'translate-x-6' : 'translate-x-0.5'
            }`}
        />
      </button>
    </div>
  );
};

// 设置项组件
const SettingsItem: React.FC<{
  icon: React.ComponentType<any>;
  label: string;
  children?: React.ReactNode;
  isLink?: boolean;
  to?: string;
  href?: string;
  onClick?: () => void;
}> = ({ icon: Icon, label, children, isLink = false, to, href, onClick }) => {
  const { theme } = useAppSettings();
  const isDark = theme === 'dark';

  const content = (
    <div
      className="flex items-center justify-between py-3 cursor-pointer"
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <Icon
          className="w-5 h-5"
          style={!isDark ? { color: '#587FFF', stroke: '#587FFF' } : { color: 'rgba(255, 255, 255, 0.7)' }}
        />
        <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
          {label}
        </span>
      </div>
      {children || (isLink && (
        <ChevronRight
          className="w-5 h-5"
          style={!isDark ? { color: '#587FFF', stroke: '#587FFF' } : { color: 'rgba(255, 255, 255, 0.7)' }}
        />
      ))}
    </div>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="block">
        {content}
      </a>
    );
  }

  if (isLink && to) {
    return <Link to={to}>{content}</Link>;
  }

  return content;
};

// 从 LANGUAGES 生成 languageOptions，保持与语音识别语言配置一致
// 将语音识别语言代码（'zh', 'en', 'es'）映射到 UI 语言代码（'zh-CN', 'en-US', 'es-ES'）
const languageOptions = Object.fromEntries(
  Object.values(LANGUAGES).map(lang => {
    const uiCode = lang.code === 'zh' ? 'zh-CN' : lang.code === 'en' ? 'en-US' : lang.code === 'es' ? 'es-ES' : lang.code;
    return [uiCode, lang.name];
  })
) as Record<string, string>;




const Profile: React.FC = () => {
  const { language, setLanguage, t, theme, notifications, setNotifications, notificationTime, setNotificationTime } = useAppSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [user, setUser] = useState<AuthUser | null>(() => loadAuthUser());
  const [hasActiveMembership, setHasActiveMembership] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [tempTime, setTempTime] = useState(notificationTime || '23:00');
  const isGuest = !user;
  const displayName = isGuest 
    ? t('profile.guest.nickname')
    : getUserDisplayName(user?.full_name || user?.username || user?.email || undefined);

  // Redirect to Splash if not logged in AND has never completed onboarding (guest or user)
  useEffect(() => {
    if (!user) {
      const hasEverCompletedOnboarding = Object.keys(localStorage).some(
        (key) => key === 'onboarding-completed' || key.startsWith('onboarding-completed_')
      );
      if (!hasEverCompletedOnboarding) {
        navigate('/splash', { replace: true });
      }
    }
  }, [user, navigate, t]);
  const handleCheckActiveMembership = async () => {
    try {
      const customerInfo = await getCustomerInfo();

      // Basic check
      const entitlements = (customerInfo as any)?.entitlements?.active;
      const hasMembership = entitlements && Object.keys(entitlements).length > 0;
      setHasActiveMembership(hasMembership);

      if (hasMembership) {
        // Parse details
        // Prioritize Platinum if both exist
        let activeEnt: any = entitlements['lumi_platinum'];
        let tier: "platinum" | "pro" = "platinum";

        if (!activeEnt) {
          activeEnt = entitlements['lumi_pro'];
          tier = "pro";
        }

        // Fallback to first available if specific keys oddly missing but active exists
        if (!activeEnt) {
          activeEnt = Object.values(entitlements)[0];
          tier = "pro";
        }

        if (activeEnt) {
          const pid = activeEnt.productIdentifier || "";
          const isAnnual = pid.includes("annual");
          const period = isAnnual ? "annual" : "monthly";

          // Format Date
          let dateStr = t('profile.subscription.dateUnknown');
          if (activeEnt.expirationDate) {
            dateStr = new Date(activeEnt.expirationDate).toLocaleDateString(undefined, {
              year: 'numeric', month: 'short', day: 'numeric'
            });
          }

          setSubscriptionDetails({
            tier,
            period,
            expiryDate: dateStr,
            willRenew: activeEnt.willRenew !== false // Default true if missing
          });
        }
      } else {
        setSubscriptionDetails(null);
      }
    } catch (e) {
      console.error("Failed to load subscription details", e);
    }
  };

  useEffect(() => {
    const loadedUser = loadAuthUser();
    setUser(loadedUser);
  }, [location.pathname]);

  useEffect(() => {
    const onFocus = () => setUser(loadAuthUser());
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  // Check active membership whenever user changes (login/logout)
  useEffect(() => {
    if (user) {
      handleCheckActiveMembership();
    } else {
      setSubscriptionDetails(null);
    }
  }, [user]);

  // Re-check membership when returning from purchase page
  useEffect(() => {
    handleCheckActiveMembership();
  }, [location.pathname, user?.id]);

  // Sync notification status with system permissions
  useEffect(() => {
    const checkPermission = async () => {
      const hasPermission = await NotificationService.hasPermission();

      if (!hasPermission && notifications) {
        // System denied, but app is ON -> Turn OFF
        setNotifications(false);
      } else if (hasPermission && !notifications) {
        // System granted, but app is OFF -> Turn ON (per user request)
        setNotifications(true);
        // Ensure schedule is active
        const [hour, minute] = (notificationTime || '23:00').split(':').map(Number);
        await NotificationService.scheduleDailyNotification(hour, minute);
      }
    };

    checkPermission();

    // Optional: Add listener for app resume if needed in future
    // App.addListener('appStateChange', ({ isActive }) => {
    //   if (isActive) checkPermission();
    // });
  }, []);

  // Handle notification toggle
  const handleNotificationToggle = async () => {
    const newStatus = !notifications;
    setNotifications(newStatus);

    if (newStatus) {
      // Turn ON: Schedule notification
      const [hour, minute] = (notificationTime || '23:00').split(':').map(Number);
      const status = await NotificationService.scheduleDailyNotification(hour, minute);

      if (status !== 'granted') {
        // Permission denied - revert toggle and ask to open settings
        setNotifications(false);
        if (window.confirm("Notifications are disabled. Would you like to open settings to enable them?")) {
          await NotificationService.openSettings();
        }
      }
    } else {
      // Turn OFF: Cancel notification
      await NotificationService.cancelDailyNotification();
    }
  };

  // Format time for display (24h -> 12h)
  const formatTimeDisplay = (timeStr: string) => {
    const [h, m] = timeStr.split(':').map(Number);
    const period = h >= 12 ? t('profile.time.pm') : t('profile.time.am');
    const hour12 = h % 12 || 12;
    return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
  };

  const handleApplyTime = async () => {
    setNotificationTime(tempTime);

    // Reschedule if notifications are enabled
    if (notifications) {
      const [hour, minute] = tempTime.split(':').map(Number);
      await NotificationService.scheduleDailyNotification(hour, minute);
    }
    setIsPickerOpen(false);
  };



  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDeleteAccount = async () => {
    try {
      // 尝试后端删除；若接口不存在，则降级为清空 nickname
      // 清空 nickname 可确保下次登录时 Splash 判定为新用户，触发 onboarding
      try {
        const email = user?.email;
        if (!email) throw new Error('No email');
        await deleteUserAccountFromBackend(email);
      } catch {
        // 后端删除失败时，降级为清空 nickname，确保下次登录触发 onboarding
        await updateUserProfile({ nickname: '' }).catch(() => {});
      }
      deleteAccount(user?.id);
      await logout();
      setUser(null);
      navigate('/splash', { replace: true, state: { forceLogin: true } });
    } catch (e) {
      console.error('Delete account error:', e);
      alert(t('profile.deleteAccount.error'));
    }
    setShowDeleteConfirm(false);
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
    navigate('/splash', { replace: true, state: { forceLogin: true } });
  };

  const animation = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
  };

  const getInitial = () => {
    if (isGuest) return 'G';
    if (displayName) {
      return displayName.charAt(0).toUpperCase();
    }
    if (user.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  return (
    <div className="fixed inset-0 w-full h-screen overflow-hidden">
      {/* 固定背景层 - 使用统一配置 */}
      <div className={`absolute inset-0 bg-gradient-to-b ${getPageBackgroundGradient(theme)}`} />

      {/* 可滚动内容层 */}
      <div className={`relative z-10 px-4 pb-44 h-full overflow-y-auto ${isDark ? 'text-white' : 'text-gray-800'}`} style={{ paddingTop: '10vh' }}>
        <div className="max-w-xl mx-auto space-y-6">
          {/* User Info */}
          <motion.div {...animation} transition={{ delay: 0.1 }}>
            <div
              className={`flex items-center gap-4 ${!isGuest ? 'cursor-pointer' : ''}`}
              onClick={() => !isGuest && navigate("/edit-account")}
            >
              {/* 头像 */}
              <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 ${isGuest ? 'bg-gray-500' : 'bg-blue-500'}`}>
                {!isGuest && user.profile_photo_url ? (
                  <img
                    src={user.profile_photo_url}
                    alt={t('profile.altProfilePhoto')}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <span className="text-2xl font-medium text-white">{getInitial()}</span>
                )}
              </div>

              {/* 用户信息 */}
              <div className="flex-1 min-w-0">
                <h2 className={`text-lg font-medium truncate ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {displayName}
                  {isGuest && (
                    <span className="ml-2 text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                      {t('profile.guest.title')}
                    </span>
                  )}
                </h2>
                {!isGuest && (
                  <>
                    {user.username && (
                      <p className={`text-sm truncate ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                        @{user.username}
                      </p>
                    )}
                    {user.email && (
                      <p className={`text-sm truncate ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                        {user.email}
                      </p>
                    )}
                  </>
                )}
                {isGuest && (
                  <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'} mt-1`}>
                    {t('profile.guest.description')}
                  </p>
                )}
              </div>

              {/* 箭头 */}
              {!isGuest && <ChevronRight className="w-5 h-5 text-white/70 flex-shrink-0" />}
            </div>
          </motion.div>

          {isGuest && (
            <motion.div {...animation} transition={{ delay: 0.15 }}>
              <div className={`p-4 rounded-2xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-blue-50 border-blue-100'}`}>
                <h3 className={`text-sm font-bold mb-3 ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                  {t('profile.guest.features.title')}
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {t('profile.guest.features.unlocked')}
                    </p>
                    <ul className={`text-xs space-y-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                      <li>• {t('whisper.settings.voice.title')}</li>
                      <li>• {t('home.playAmbient')}</li>
                      <li>• {t('profile.notifications.manage')}</li>
                    </ul>
                  </div>
                  <div className="space-y-2">
                    <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? 'text-blue-400/60' : 'text-blue-600/60'}`}>
                      {t('profile.guest.features.locked')}
                    </p>
                    <ul className={`text-xs space-y-1 ${isDark ? 'text-blue-300/80' : 'text-blue-700/80'}`}>
                      <li className="font-bold">• {t('whisper.title')}</li>
                      {/* <li>• {t('profile.subscription.active')}</li> */}
                      <li className="font-bold">• {t('journals.title')}</li>
                    </ul>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/splash', { replace: true, state: { forceLogin: true } })}
                  className="w-full mt-4 py-2.5 rounded-xl bg-blue-500 text-white text-sm font-bold hover:bg-blue-600 transition-colors shadow-lg shadow-blue-500/20"
                >
                  {t('profile.guest.loginAction')}
                </button>
              </div>
            </motion.div>
          )}

          {!isGuest && (
            <motion.div {...animation} transition={{ delay: 0.2 }}>
              <MembershipCard hasActiveMembership={hasActiveMembership} details={subscriptionDetails} />
            </motion.div>
          )}

          {/* General Section */}
          <motion.div {...animation} transition={{ delay: 0.3 }}>
            <div className="space-y-4">
              {/* Section Title */}
              <div className={`border-t pt-4 ${isDark ? 'border-white/20' : 'border-gray-300'}`}>
                <h2 className={`text-sm font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {t('profile.general')}
                </h2>
              </div>

              {/* Invite Friends */}
              <SettingsItem
                icon={Gift}
                label={t('profile.inviteFriends')}
                isLink={true}
                to="/referral"
              >
                <div className="bg-gradient-to-r from-orange-400 to-pink-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                  {t('profile.free')}
                </div>
              </SettingsItem>

              {/* Language */}
              <SettingsItem icon={Languages} label={t('profile.language')}>
                <div className="relative">
                  <select
                    value={language}
                    onChange={(e) => {
                      setLanguage(e.target.value);
                      alert(t('profile.language.updated', { lang: languageOptions[e.target.value as keyof typeof languageOptions] }));
                    }}
                    className={`text-sm font-medium focus:outline-none rounded-full px-4 py-2 pr-10 appearance-none cursor-pointer w-full ${isDark ? 'text-white' : 'text-gray-800'
                      }`}
                    aria-label={t('profile.altSelectLanguage')}
                    style={{
                      background: isDark
                        ? 'linear-gradient(-3deg, rgba(255, 255, 255, 0.1) 0%, rgba(171, 171, 255, 0.2) 50%)'
                        : 'linear-gradient(-3deg, rgba(255, 255, 255, 0.3) 0%, rgba(173, 225, 255, 0.2) 50%)',
                      backdropFilter: 'blur(90px) saturate(110%)',
                      WebkitBackdropFilter: 'blur(90px) saturate(110%)',
                      border: isDark ? '0.1px solid rgba(255, 255, 255, 0.1)' : '0.1px solid rgba(255, 255, 255, 0.5)',
                      boxShadow: isDark
                        ? `
                        inset 0 0.5px 1px 0 rgba(0, 0, 0, 0.3),
                        inset 0.5px 0 1px 0 rgba(0, 0, 0, 0.3),
                        inset 0 -0.5px 1px 0 rgba(255, 255, 255, 0.1),
                        inset -0.5px 0 1px 0 rgba(255, 255, 255, 0.1),
                        0 4px 16px 0 rgba(0, 0, 0, 0.3)
                      `
                        : `
                        inset 0 0.5px 1px 0 rgba(255, 255, 255, 0.6),
                        inset 0.5px 0 1px 0 rgba(255, 255, 255, 0.6),
                        inset 0 -0.5px 1px 0 rgba(0, 0, 0, 0.1),
                        inset -0.5px 0 1px 0 rgba(0, 0, 0, 0.1),
                        0 4px 16px 0 rgba(0, 0, 0, 0.05)
                      `,
                    }}
                  >
                    {Object.entries(languageOptions)
                      .filter(([code]) => code !== 'zh-CN')
                      .map(([code, name]) =>
                        <option
                          key={code}
                          value={code}
                          className={isDark ? 'bg-[#02082f] text-white' : 'bg-white text-gray-800'}
                        >
                          {name}
                        </option>
                      )}
                  </select>
                  {/* 下拉箭头图标 */}
                  <div
                    className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    style={{
                      width: '12px',
                      height: '12px',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='${isDark ? 'white' : '%23587FFF'}' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      backgroundSize: 'contain',
                    }}
                  />
                </div>
              </SettingsItem>

              {/* Dark Mode */}
              <ThemeToggle />

              {/* Manage Notification */}
              <div className="space-y-0">
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className={`absolute -inset-1 rounded-full blur-sm opacity-20 ${isDark ? 'bg-blue-400' : 'bg-blue-600'}`}></div>
                      <Bell
                        className="w-5 h-5 relative"
                        style={!isDark ? { color: '#587FFF', stroke: '#587FFF' } : { color: 'rgba(255, 255, 255, 0.7)' }}
                      />
                    </div>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {t('profile.notifications.manage')}
                    </span>
                  </div>
                  <button
                    onClick={handleNotificationToggle}
                    className={`w-12 h-6 rounded-full transition-colors ${notifications ? 'bg-blue-500' : 'bg-gray-300'
                      }`}
                  >
                    <div
                      className={`w-5 h-5 bg-white rounded-full transition-transform ${notifications ? 'translate-x-6' : 'translate-x-0.5'
                        }`}
                    />
                  </button>
                </div>

                {/* Extended Section for Time Picker */}
                <AnimatePresence>
                  {notifications && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className={`mt-1 mb-3 rounded-xl overflow-hidden ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                        {/* Bed Time Row - Clickable */}
                        <div
                          className="p-4 flex items-center justify-between cursor-pointer"
                          onClick={() => {
                            setTempTime(notificationTime || '23:00');
                            setIsPickerOpen(!isPickerOpen);
                          }}
                        >
                          <span className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                            {t('profile.notifications.bedTime')}
                          </span>
                          <span className={`text-lg font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                            {formatTimeDisplay(notificationTime || '23:00')}
                          </span>
                        </div>

                        {/* Friendly Message */}
                        <div className="px-4 pb-3 -mt-2">
                          <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                            {t('profile.notifications.reminderHint')}
                          </p>
                        </div>

                        {/* Expanded Picker */}
                        <AnimatePresence>
                          {isPickerOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                            >
                              <div className="pb-4 px-4">
                                <TimePicker
                                  value={tempTime}
                                  onChange={setTempTime}
                                  isDark={isDark}
                                />
                                <div className="flex justify-end mt-4">
                                  <button
                                    onClick={handleApplyTime}
                                    className="text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors"
                                  >
                                    {t('profile.notifications.apply')}
                                  </button>
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>

          {/* About Section */}
          <motion.div {...animation} transition={{ delay: 0.4 }}>
            <div className="space-y-0">
              {/* Section Title */}
              <div className={`border-t pt-4 ${isDark ? 'border-white/20' : 'border-gray-300'}`}>
                <h2 className={`text-sm font-medium mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>
                  {t('profile.about')}
                </h2>
              </div>

              {/*  Terms of Use Policy */}
              <SettingsItem
                icon={FileText}
                label={t('profile.termsOfUsePolicy')}
                isLink={true}
                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
              />

              {/* Privacy Policy */}
              <SettingsItem
                icon={Shield}
                label={t('profile.privacyPolicy')}
                isLink={true}
                href="https://pli477.github.io/apple_policy_website/"
              />

              {/* About Lumi */}
              <SettingsItem
                icon={Star}
                label={t('profile.aboutLumi')}
                isLink={true}
                href="https://in-no-work.org/program"
              />
            </div>
          </motion.div>

          {/* Logout */}
          {!isGuest && (
            <motion.div {...animation} transition={{ delay: 0.5 }} className="space-y-3">
              <button
                onClick={handleLogout}
                className="w-full rounded-xl p-4 flex items-center justify-center gap-3 transition-all border border-white/25 bg-white/10 hover:bg-white/15 shadow-lg backdrop-blur-xl"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">{t('profile.logout')}</span>
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full rounded-xl p-4 flex items-center justify-center gap-3 transition-all border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 shadow-lg backdrop-blur-xl text-red-400"
              >
                <Trash2 className="w-5 h-5" />
                <span className="text-sm font-medium">{t('profile.deleteAccount')}</span>
              </button>
            </motion.div>
          )}

          {/* Delete Account Confirmation Dialog */}
          {showDeleteConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6">
              <div className="w-full max-w-sm rounded-2xl bg-gray-900 border border-white/15 p-6 space-y-4 shadow-2xl">
                <h2 className="text-lg font-semibold text-white">{t('profile.deleteAccount.confirmTitle')}</h2>
                <p className="text-sm text-white/70 leading-relaxed">{t('profile.deleteAccount.confirmMessage')}</p>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1 py-3 rounded-xl border border-white/20 text-white text-sm font-medium hover:bg-white/10 transition-colors"
                  >
                    {t('profile.deleteAccount.cancel')}
                  </button>
                  <button
                    onClick={handleDeleteAccount}
                    className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
                  >
                    {t('profile.deleteAccount.confirm')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Admin Tools - Only visible to admins */}
          {user?.is_admin && (
            <motion.div {...animation} transition={{ delay: 0.6 }} className="pt-6 border-t border-red-500/30">
              <div className="flex items-center gap-2 mb-4">
                 <Shield className="w-5 h-5 text-red-400" />
                 <h2 className="text-red-400 text-sm font-bold uppercase tracking-wider">Admin Tools</h2>
              </div>
              
              <div className="space-y-4">
                <AdminLocalStorageManager user={user} />
                
                <button
                  onClick={() => {
                    const userId = user.id;
                    const keys = [
                      `onboarding-completed_${userId}`,
                      `onboarding-data_${userId}`,
                      `t2s_conversations_${userId}`,
                      `t2s_daily_summaries_${userId}`,
                      'onboarding-completed',
                      'onboarding-data',
                      't2s_conversations',
                      't2s_daily_summaries',
                      'auth-user' // Clear local user cache (nickname/is_admin) too
                    ];
                    
                    // 1. Clear keys
                    keys.forEach(k => localStorage.removeItem(k));
                    
                    // 2. Set force-onboarding flag for Splash to skip recovery
                    localStorage.setItem('t2s_debug_force_onboarding', '1');
                    
                    // 3. Force redirect to splash flow
                    window.location.href = '/';
                  }}
                  className="w-full py-3 px-4 rounded-xl bg-red-500/20 border border-red-500/50 text-red-200 text-sm font-medium hover:bg-red-500/30 transition-colors"
                >
                  Reset App State (Wipe Local)
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Admin component to manage local storage keys
 */
const AdminLocalStorageManager: React.FC<{ user: AuthUser }> = ({ user }) => {
  const [items, setItems] = useState<{ key: string, label: string, value: string }[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const refreshItems = () => {
    const keys = [
      { key: `onboarding-completed_${user.id}`, label: 'Onboarding Completed' },
      { key: `onboarding-data_${user.id}`, label: 'Onboarding Data (User)' },
      { key: 'onboarding-data', label: 'Onboarding Data (Guest)' },
      { key: 'auth-user', label: 'Auth User (Cache)' },
      { key: 't2s_conversations', label: 'Conversations' },
      { key: 't2s_daily_summaries', label: 'Daily Summaries' }
    ];

    const newItems = keys.map(k => ({
      ...k,
      value: localStorage.getItem(k.key) || "—"
    }));
    setItems(newItems);
  };

  useEffect(() => {
    refreshItems();
  }, [user.id]);

  const handleSave = () => {
    if (editingKey) {
      if (editValue === "—" || editValue === "") {
        localStorage.removeItem(editingKey);
      } else {
        localStorage.setItem(editingKey, editValue);
      }
      setEditingKey(null);
      refreshItems();
    }
  };

  return (
    <div className="bg-black/20 rounded-xl p-4 space-y-3 border border-white/5">
      <h3 className="text-xs text-white/50 font-medium">Local Storage Inspector</h3>
      <div className="space-y-2">
        {items.map(item => (
          <div key={item.key} className="flex flex-col gap-1 boder-b border-white/5 pb-2 last:border-0">
            <div className="flex justify-between items-center">
              <span className="text-[10px] text-blue-300 font-mono">{item.label}</span>
              <button 
                onClick={() => {
                  setEditingKey(item.key);
                  setEditValue(item.value === "—" ? "" : item.value);
                }}
                className="text-[10px] bg-white/10 px-2 py-0.5 rounded hover:bg-white/20"
              >
                Edit
              </button>
            </div>
            {editingKey === item.key ? (
              <div className="flex gap-2 mt-1">
                <input 
                  className="flex-1 bg-white/10 rounded px-2 py-1 text-xs text-white outline-none border border-blue-500/50"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  autoFocus
                />
                <button onClick={handleSave} className="text-[10px] text-green-400">Save</button>
                <button onClick={() => setEditingKey(null)} className="text-[10px] text-white/40">Cancel</button>
              </div>
            ) : (
              <div className="text-[10px] text-white/80 break-all bg-black/40 p-1.5 rounded font-mono max-h-20 overflow-y-auto">
                {item.value}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Profile;
